import { useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { logoUrl, useSettings, useSettingsRefresh } from "@/lib/settings";
import { Upload, FloppyDisk, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import FxPanel from "@/components/FxPanel";

export default function AdminBranding() {
  const live = useSettings();
  const refresh = useSettingsRefresh();
  const [form, setForm] = useState(live);
  const [saving, setSaving] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [faviconFile, setFaviconFile] = useState(null);

  useEffect(() => { setForm(live); }, [live]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const updSocial = (k, v) => setForm((f) => ({ ...f, social: { ...(f.social || {}), [k]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        brand_name: form.brand_name, legal_name: form.legal_name,
        tagline: form.tagline, description: form.description,
        support_email: form.support_email, phone: form.phone,
        secondary_email: form.secondary_email,
        address_lines: form.address_lines,
        footer_text: form.footer_text,
        meta_title: form.meta_title, meta_description: form.meta_description,
        og_image_url: form.og_image_url,
        social: form.social, terms_url: form.terms_url, privacy_url: form.privacy_url,
        invoice_prefix: form.invoice_prefix, proposal_prefix: form.proposal_prefix,
        established_year: form.established_year,
        service_fee: form.service_fee,
        interview_calendar_url: form.interview_calendar_url,
        pricing_disclaimer_short: form.pricing_disclaimer_short,
        pricing_disclaimer_long: form.pricing_disclaimer_long,
        company_formation_fee_usd: Number(form.company_formation_fee_usd ?? 100),
        company_formation_fee_enabled: !!form.company_formation_fee_enabled,
        company_formation_fee_overrides: form.company_formation_fee_overrides || {},
        fx_rates_usd_to: form.fx_rates_usd_to || {},
        calculator_currency_overrides: form.calculator_currency_overrides || {},
        google_ads_id: form.google_ads_id || "",
        ga4_measurement_id: form.ga4_measurement_id || "",
        gtm_container_id: form.gtm_container_id || "",
        clarity_project_id: form.clarity_project_id || "",
        meta_pixel_id: form.meta_pixel_id || "",
        ga4_dashboard_url: form.ga4_dashboard_url || "",
        whatsapp_number: form.whatsapp_number || "",
        conversion_values_usd: form.conversion_values_usd || {},
        conversion_labels: form.conversion_labels || {},
        homepage_hero: form.homepage_hero || {},
        fx_auto_refresh_enabled: !!form.fx_auto_refresh_enabled,
        fx_alert_threshold_pct: Number(form.fx_alert_threshold_pct ?? 5),
      };
      await api.put("/admin/settings", payload);
      if (logoFile) {
        const fd = new FormData(); fd.append("file", logoFile);
        await api.post("/admin/settings/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setLogoFile(null);
      }
      if (faviconFile) {
        const fd = new FormData(); fd.append("file", faviconFile);
        await api.post("/admin/settings/favicon", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setFaviconFile(null);
      }
      await refresh();
      toast.success("Branding saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const clearLogo = async () => {
    if (!window.confirm("Remove current logo?")) return;
    await api.delete("/admin/settings/logo");
    await refresh();
    toast.success("Logo cleared");
  };
  const clearFavicon = async () => {
    if (!window.confirm("Remove current favicon?")) return;
    await api.delete("/admin/settings/favicon");
    await refresh();
    toast.success("Favicon cleared");
  };

  return (
    <div data-testid="admin-branding">
      <div className="flex justify-between items-start mb-6">
        <div>
          <div className="overline mb-2">White-label branding</div>
          <h2 className="font-display text-2xl text-gos-charcoal">Make the platform yours</h2>
          <p className="text-sm text-gos-muted mt-2 max-w-xl">Every public surface — site, portal, emails, PDFs, OG tags — updates live when you save.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="branding-save-btn" className="btn-primary text-xs !py-2">
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Brand identity */}
        <Section title="Brand identity">
          <Field label="Brand name" value={form.brand_name} onChange={(v) => upd("brand_name", v)} testid="branding-name" />
          <Field label="Legal name" value={form.legal_name} onChange={(v) => upd("legal_name", v)} testid="branding-legal" />
          <Field label="Tagline" value={form.tagline} onChange={(v) => upd("tagline", v)} testid="branding-tagline" />
          <TextArea label="Description" value={form.description} onChange={(v) => upd("description", v)} testid="branding-desc" />
        </Section>

        {/* Contact */}
        <Section title="Contact">
          <Field label="Support email" value={form.support_email} onChange={(v) => upd("support_email", v)} testid="branding-email" />
          <Field label="Secondary email (optional)" value={form.secondary_email} onChange={(v) => upd("secondary_email", v)} testid="branding-email-2" />
          <Field label="Phone" value={form.phone} onChange={(v) => upd("phone", v)} testid="branding-phone" />
          <TextArea label="Address (one line per row)" value={(form.address_lines || []).join("\n")} onChange={(v) => upd("address_lines", v.split("\n").filter(Boolean))} testid="branding-address" rows={4} />
          <Field label="Footer text" value={form.footer_text} onChange={(v) => upd("footer_text", v)} testid="branding-footer" />
          <Field label="Discovery call calendar URL (Calendly / Cal.com)" value={form.interview_calendar_url || ""} onChange={(v) => upd("interview_calendar_url", v)} testid="branding-calendar-url" />
        </Section>

        {/* Pricing disclaimer */}
        <Section title="Pricing disclaimer">
          <TextArea label="Short disclaimer (shown on cards & inline notes)" value={form.pricing_disclaimer_short || ""} onChange={(v) => upd("pricing_disclaimer_short", v)} testid="branding-disclaimer-short" rows={3} />
          <TextArea label="Long disclaimer (shown on pricing pages, proposals, calculator)" value={form.pricing_disclaimer_long || ""} onChange={(v) => upd("pricing_disclaimer_long", v)} testid="branding-disclaimer-long" rows={6} />
        </Section>

        {/* SEO */}
        <Section title="SEO & metadata">
          <Field label="Meta title" value={form.meta_title} onChange={(v) => upd("meta_title", v)} testid="branding-meta-title" />
          <TextArea label="Meta description" value={form.meta_description} onChange={(v) => upd("meta_description", v)} testid="branding-meta-desc" />
          <Field label="Open Graph image URL" value={form.og_image_url} onChange={(v) => upd("og_image_url", v)} testid="branding-og" />
          <Field label="Terms URL" value={form.terms_url} onChange={(v) => upd("terms_url", v)} testid="branding-terms" />
          <Field label="Privacy URL" value={form.privacy_url} onChange={(v) => upd("privacy_url", v)} testid="branding-privacy" />
        </Section>

        {/* Social */}
        <Section title="Social links">
          {["twitter", "linkedin", "facebook", "instagram", "youtube"].map((s) => (
            <Field key={s} label={s} value={form.social?.[s] || ""} onChange={(v) => updSocial(s, v)} testid={`branding-social-${s}`} />
          ))}
        </Section>

        {/* Logo */}
        <Section title="Logo (PNG/SVG)">
          {logoUrl(live) ? (
            <div className="flex items-center gap-3">
              <img src={logoUrl(live)} alt="logo" className="h-12 bg-gos-subtle p-2 rounded-sm" />
              <button onClick={clearLogo} data-testid="branding-logo-clear" className="text-xs text-gos-error hover:underline">Remove</button>
            </div>
          ) : (
            <div className="text-xs text-gos-muted">No logo uploaded — using wordmark fallback.</div>
          )}
          <FilePick label="Replace logo" testid="branding-logo-input" accept="image/*" onPick={setLogoFile} file={logoFile} />
          <p className="text-[10px] text-gos-muted">Max 2 MB. Recommended height 80–120 px. Shown in header, footer, login screen, and PDF header.</p>
        </Section>

        {/* Favicon */}
        <Section title="Favicon (ICO/PNG/SVG)">
          {live.favicon_url ? (
            <div className="flex items-center gap-3">
              <img src={live.favicon_url.startsWith("http") ? live.favicon_url : `${BACKEND_URL}${live.favicon_url}?v=${Date.now()}`} alt="favicon" className="h-8 w-8 bg-gos-subtle p-1 rounded-sm" />
              <button onClick={clearFavicon} data-testid="branding-favicon-clear" className="text-xs text-gos-error hover:underline">Remove</button>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-gos-muted"><ImageIcon size={20} /> Default favicon in use.</div>
          )}
          <FilePick label="Replace favicon" testid="branding-favicon-input" accept="image/*,.ico" onPick={setFaviconFile} file={faviconFile} />
          <p className="text-[10px] text-gos-muted">Max 2 MB. Square format recommended (32×32 or 64×64).</p>
        </Section>

        {/* Document numbering */}
        <Section title="Document numbering">
          <Field label="Invoice number prefix" value={form.invoice_prefix} onChange={(v) => upd("invoice_prefix", v)} testid="branding-invoice-prefix" />
          <Field label="Proposal number prefix" value={form.proposal_prefix} onChange={(v) => upd("proposal_prefix", v)} testid="branding-proposal-prefix" />
          <Field label="Established year (shown as 'Since YYYY')" value={form.established_year ?? ""} onChange={(v) => upd("established_year", v === "" ? null : Number(v))} testid="branding-established" />
          <Field label="Default service fee (applied to every quote)" value={form.service_fee ?? ""} onChange={(v) => upd("service_fee", v === "" ? null : Number(v))} testid="branding-service-fee" />
          <Field label="Discovery call calendar URL (Calendly / Cal.com)" value={form.interview_calendar_url || ""} onChange={(v) => upd("interview_calendar_url", v)} testid="branding-calendar-url" />
          <p className="text-[10px] text-gos-muted">Used for new invoices (e.g. INV-0001) and proposals (e.g. PRO-0001). Service fee is applied as a one-time line in the selected country&apos;s currency.</p>
        </Section>

        {/* Pricing disclaimer */}
        <Section title="Pricing disclaimer">
          <TextArea label="Short disclaimer (shown on cards & inline notes)" value={form.pricing_disclaimer_short || ""} onChange={(v) => upd("pricing_disclaimer_short", v)} testid="branding-disclaimer-short" rows={3} />
          <TextArea label="Long disclaimer (shown on pricing pages, proposals, calculator)" value={form.pricing_disclaimer_long || ""} onChange={(v) => upd("pricing_disclaimer_long", v)} testid="branding-disclaimer-long" rows={6} />
        </Section>

        {/* Company formation fee + FX */}
        <Section title="Company formation fee">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid="branding-formation-enabled" checked={!!form.company_formation_fee_enabled}
              onChange={(e) => upd("company_formation_fee_enabled", e.target.checked)} /> Apply the formation fee globally
          </label>
          <Field label="Company formation fee (USD)" value={form.company_formation_fee_usd ?? 100} onChange={(v) => upd("company_formation_fee_usd", v === "" ? null : Number(v))} testid="branding-formation-fee" />
          <p className="text-[10px] text-gos-muted">Added only when the company-registration service is in the quote. Converted to the country currency using the FX rates below.</p>
          <KVTable
            label="FX rates (1 USD = X)"
            value={form.fx_rates_usd_to || {}}
            onChange={(obj) => upd("fx_rates_usd_to", obj)}
            keyLabel="currency"
            valueLabel="rate"
            isNumeric
            testid="branding-fx-rates"
          />
          <KVTable
            label="Formation-fee per-country override (amount already in the country currency)"
            value={Object.fromEntries(Object.entries(form.company_formation_fee_overrides || {}).map(([k, v]) => [k, v?.amount ?? v]))}
            onChange={(obj) => upd("company_formation_fee_overrides", Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, { amount: Number(v) || 0 }])))}
            keyLabel="country slug"
            valueLabel="amount"
            isNumeric
            testid="branding-formation-overrides"
          />
          <KVTable
            label="Calculator currency override (country → display currency for calculator only)"
            value={form.calculator_currency_overrides || {}}
            onChange={(obj) => upd("calculator_currency_overrides", obj)}
            keyLabel="country slug"
            valueLabel="currency"
            testid="branding-calc-override"
          />
          <p className="text-[10px] text-gos-muted">Example: <code>canada</code> → <code>USD</code> means the calculator returns USD for Canada quotes even though the country pages show CAD.</p>

          {/* Auto-refresh toggle + panel */}
          <div className="pt-3 border-t border-gos-border space-y-3">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" data-testid="branding-fx-auto-refresh" checked={!!form.fx_auto_refresh_enabled}
                onChange={(e) => upd("fx_auto_refresh_enabled", e.target.checked)} />
              <span>Auto-refresh FX rates daily (free, ECB-fed via open.er-api.com)</span>
            </label>
            <Field label="Alert threshold % (notify if any rate moves more than this)" value={form.fx_alert_threshold_pct ?? 5} onChange={(v) => upd("fx_alert_threshold_pct", v === "" ? null : Number(v))} testid="branding-fx-threshold" />
            <FxPanel />
          </div>
        </Section>

        {/* Analytics & Tracking */}
        <Section title="Analytics & tracking">
          <p className="text-[10px] text-gos-muted">Paste your IDs below. Scripts only load when an ID is present. Empty = not tracked.</p>
          <Field label="Google Ads ID (AW-XXXXXXXXXX)" value={form.google_ads_id || ""} onChange={(v) => upd("google_ads_id", v)} testid="branding-google-ads" />
          <Field label="GA4 Measurement ID (G-XXXXXXXXXX)" value={form.ga4_measurement_id || ""} onChange={(v) => upd("ga4_measurement_id", v)} testid="branding-ga4" />
          <Field label="Google Tag Manager ID (GTM-XXXXXXX)" value={form.gtm_container_id || ""} onChange={(v) => upd("gtm_container_id", v)} testid="branding-gtm" />
          <Field label="Microsoft Clarity Project ID" value={form.clarity_project_id || ""} onChange={(v) => upd("clarity_project_id", v)} testid="branding-clarity" />
          <Field label="Meta Pixel ID" value={form.meta_pixel_id || ""} onChange={(v) => upd("meta_pixel_id", v)} testid="branding-meta-pixel" />
          <Field label="GA4 dashboard URL (for the 'View Google Analytics' button)" value={form.ga4_dashboard_url || ""} onChange={(v) => upd("ga4_dashboard_url", v)} testid="branding-ga4-link" />
          <Field label="WhatsApp number (digits only, with country code)" value={form.whatsapp_number || ""} onChange={(v) => upd("whatsapp_number", v.replace(/\D/g, ""))} testid="branding-whatsapp" />
          <KVTable
            label="Conversion values (USD) — admin editable"
            value={form.conversion_values_usd || {}}
            onChange={(obj) => upd("conversion_values_usd", obj)}
            keyLabel="event"
            valueLabel="value (USD)"
            isNumeric
            testid="branding-conversion-values"
          />
          <KVTable
            label="Google Ads conversion labels (event → label, appended to Ads ID)"
            value={form.conversion_labels || {}}
            onChange={(obj) => upd("conversion_labels", obj)}
            keyLabel="event"
            valueLabel="label"
            testid="branding-conversion-labels"
          />
          <p className="text-[10px] text-gos-muted">Final tag fires as <code>send_to: AW-XXXX/LABEL</code>. Supported events: <code>lead</code>, <code>franchise_application</code>, <code>brochure_request</code>, <code>calculator_quote</code>, <code>consultation</code>, <code>whatsapp_click</code>, <code>phone_click</code>, <code>payment_completed</code>, <code>client_registration</code>. Empty label = generic event only (no Ads conversion).</p>
        </Section>

        {/* Homepage hero — animation & copy */}
        <Section title="Homepage hero (animation & copy)">
          <HeroEditor
            value={form.homepage_hero || {}}
            onChange={(v) => upd("homepage_hero", v)}
          />
        </Section>
      </div>

      <div className="mt-8 bg-gos-subtle border border-gos-border rounded-sm p-4 text-xs text-gos-slate leading-relaxed">
        <strong>Tip:</strong> White-labeling is now live across the public site, client portal, admin shell, emails, and PDF documents. The Stripe receipt page is the only branded touch you don&apos;t directly control — that&apos;s served by Stripe (your account name appears there).
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="bg-white border border-gos-border rounded-sm p-5 space-y-3">
      <div className="overline">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">{label}</label>
      <input data-testid={testid} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy" />
    </div>
  );
}

function TextArea({ label, value, onChange, testid, rows = 3 }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">{label}</label>
      <textarea data-testid={testid} value={value ?? ""} rows={rows} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy" />
    </div>
  );
}

function HeroEditor({ value, onChange }) {
  const h = value || {};
  const set = (k, v) => onChange({ ...h, [k]: v });
  const scenes = Array.isArray(h.scene_captions) ? h.scene_captions : ["", "", "", "", ""];
  const countries = Array.isArray(h.expansion_countries) ? h.expansion_countries : [];
  const trust = Array.isArray(h.trust_indicators) ? h.trust_indicators : [];

  return (
    <div className="space-y-4">
      {/* Toggle row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <label className="flex items-center gap-2 text-sm text-gos-slate">
          <input
            type="checkbox"
            checked={h.animation_enabled !== false}
            onChange={(e) => set("animation_enabled", e.target.checked)}
            data-testid="hero-animation-enabled"
          /> Animation enabled
        </label>
        <label className="flex items-center gap-2 text-sm text-gos-slate">
          <input
            type="checkbox"
            checked={h.mobile_simplified !== false}
            onChange={(e) => set("mobile_simplified", e.target.checked)}
            data-testid="hero-mobile-simplified"
          /> Use simplified mobile version
        </label>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Animation speed (0.5–2.0)</label>
          <input
            type="number" step="0.1" min="0.5" max="2"
            value={h.animation_speed ?? 1.0}
            onChange={(e) => set("animation_speed", Number(e.target.value))}
            className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm"
            data-testid="hero-animation-speed"
          />
        </div>
      </div>

      <Field label="Fallback image URL (shown when animation disabled or reduced-motion)" value={h.fallback_image_url || ""} onChange={(v) => set("fallback_image_url", v)} testid="hero-fallback-url" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Headline — line 1" value={h.headline_line1 || ""} onChange={(v) => set("headline_line1", v)} testid="hero-headline-1" />
        <Field label="Headline — line 2 (italic)" value={h.headline_line2 || ""} onChange={(v) => set("headline_line2", v)} testid="hero-headline-2" />
      </div>
      <TextArea label="Subheadline" value={h.subheadline || ""} onChange={(v) => set("subheadline", v)} testid="hero-sub" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="CTA 1 label" value={h.cta_primary_label || ""} onChange={(v) => set("cta_primary_label", v)} testid="hero-cta1-label" />
        <Field label="CTA 1 URL" value={h.cta_primary_url || ""} onChange={(v) => set("cta_primary_url", v)} testid="hero-cta1-url" />
        <Field label="CTA 2 label" value={h.cta_secondary_label || ""} onChange={(v) => set("cta_secondary_label", v)} testid="hero-cta2-label" />
        <Field label="CTA 2 URL" value={h.cta_secondary_url || ""} onChange={(v) => set("cta_secondary_url", v)} testid="hero-cta2-url" />
        <Field label="CTA 3 label" value={h.cta_tertiary_label || ""} onChange={(v) => set("cta_tertiary_label", v)} testid="hero-cta3-label" />
        <Field label="CTA 3 URL" value={h.cta_tertiary_url || ""} onChange={(v) => set("cta_tertiary_url", v)} testid="hero-cta3-url" />
      </div>

      <Field
        label="Trust indicators (comma-separated)"
        value={trust.join(", ")}
        onChange={(v) => set("trust_indicators", v.split(",").map((s) => s.trim()).filter(Boolean))}
        testid="hero-trust"
      />

      <div>
        <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">Scene captions (5 lines — one per scene)</label>
        <div className="space-y-2">
          {[0, 1, 2, 3, 4].map((i) => (
            <input
              key={i}
              value={scenes[i] || ""}
              placeholder={["Asia entrepreneur", "Connecting to Canada", "Setup in Canada", "Business operating", "Global expansion"][i]}
              onChange={(e) => {
                const next = [...scenes];
                next[i] = e.target.value;
                set("scene_captions", next);
              }}
              className="w-full border border-gos-border rounded-sm px-2 py-1.5 text-sm"
              data-testid={`hero-scene-${i}`}
            />
          ))}
        </div>
      </div>

      <Field
        label="Expansion countries shown in scene 5 (comma-separated — must match: USA, UK, UAE, Saudi Arabia, Malaysia, India, Indonesia, Argentina, Australia)"
        value={countries.join(", ")}
        onChange={(v) => set("expansion_countries", v.split(",").map((s) => s.trim()).filter(Boolean))}
        testid="hero-expansion"
      />
    </div>
  );
}

function KVTable({ label, value, onChange, keyLabel = "key", valueLabel = "value", isNumeric = false, testid }) {
  const entries = Object.entries(value || {});
  const updateEntry = (idx, newKey, newVal) => {
    const next = {};
    entries.forEach(([k, v], i) => {
      if (i === idx) next[newKey] = isNumeric ? Number(newVal) || 0 : newVal;
      else next[k] = v;
    });
    onChange(next);
  };
  const remove = (idx) => {
    const next = {};
    entries.forEach(([k, v], i) => { if (i !== idx) next[k] = v; });
    onChange(next);
  };
  const add = () => onChange({ ...(value || {}), "": isNumeric ? 0 : "" });

  return (
    <div data-testid={testid}>
      <label className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">{label}</label>
      <div className="border border-gos-border rounded-sm overflow-hidden">
        {entries.length === 0 && (
          <div className="text-xs text-gos-muted text-center p-3">No entries — click <em>Add row</em>.</div>
        )}
        {entries.map(([k, v], i) => (
          <div key={`${testid}-${i}`} className="grid grid-cols-12 gap-2 px-2 py-1.5 border-b border-gos-border last:border-b-0">
            <input value={k} placeholder={keyLabel}
              onChange={(e) => updateEntry(i, e.target.value, v)}
              data-testid={`${testid}-key-${i}`}
              className="col-span-5 border border-gos-border rounded-sm px-2 py-1 text-xs" />
            <input value={v} placeholder={valueLabel} type={isNumeric ? "number" : "text"}
              onChange={(e) => updateEntry(i, k, e.target.value)}
              data-testid={`${testid}-val-${i}`}
              className="col-span-6 border border-gos-border rounded-sm px-2 py-1 text-xs" />
            <button type="button" onClick={() => remove(i)} data-testid={`${testid}-del-${i}`}
              className="col-span-1 text-rose-600 text-sm hover:bg-rose-50 rounded-sm">×</button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} data-testid={`${testid}-add`}
        className="btn-secondary text-[11px] !py-1 mt-2">+ Add row</button>
    </div>
  );
}


function FilePick({ label, onPick, file, testid, accept }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-widest text-gos-muted mb-1">{label}</span>
      <div className="flex items-center gap-2">
        <label data-testid={`${testid}-trigger`} className="btn-secondary text-xs !py-2 !px-3 cursor-pointer">
          <Upload size={14} weight="bold" /> Choose file
          <input type="file" hidden accept={accept} data-testid={testid} onChange={(e) => onPick(e.target.files?.[0] || null)} />
        </label>
        <span className="text-xs text-gos-muted truncate">{file?.name || "no file selected"}</span>
      </div>
    </label>
  );
}
